import * as React from 'react'
import { observer } from 'mobx-react'
import { filter } from 'rxjs/operators'
import CallRootViewModel from 'messaging/calling/CallRootViewModel'
import { CallOperatorState } from 'messaging/calling/CallViewModel'
import CallPrompt from '../CallPrompt'
import CallManager from '../CallManager'
import { AudioRenderer } from '../MediaRenderer'
import styles from './CallPromptsHost.sass'

interface CallPromptsHostProps {
  vm: CallRootViewModel
}

/**
 * The CallPromptsHost
 */
export default observer(function CallPromptsHost({ vm }: CallPromptsHostProps) {
  const [position, setPosition] = React.useState({ x: 0, y: 0 })
  const positionManagement = React.useMemo(
    () => ({
      initialPosition: position,
      onSavePosition: setPosition,
    }),
    [position, setPosition]
  )
  const reset = React.useCallback(() => {
    setPosition({ x: 0, y: 0 })
  }, [setPosition])

  React.useEffect(() => {
    if (!vm.activeCall) {
      return
    }

    const subscription = vm.activeCall.state$
      .pipe(filter((state) => state === CallOperatorState.Connecting))
      .subscribe(reset)

    return () => subscription.unsubscribe()
  }, [vm.activeCall])

  return (
    <div className={styles.root}>
      {vm.calls
        .filter((call) => call.state === CallOperatorState.Incoming)
        .map((call) => (
          <div className={styles.callPrompt} key={call.call.id}>
            <CallPrompt shadowed call={call.call} />
          </div>
        ))}
      {vm.calls
        .filter(
          (call) =>
            call.state === CallOperatorState.Connected &&
            !call.activeCallRendered
        )
        .map((call) => {
          return (
            <div className={styles.callManager} key={call.call.id}>
              <CallManager
                call={call.call}
                positionManagement={positionManagement}
              />
            </div>
          )
        })}
      {/*  Hosts the active call's audio tracks */}
      {vm.activeCall?.participants.map((p) => (
        <AudioRenderer key={p.participant.userId} vm={p} />
      ))}
    </div>
  )
})
